import { useCallback, useState } from "react";

import { TableKey } from "./types";

interface UseExpansionResult {
    /** Read-only snapshot of expanded parent keys. */
    expandedGroups: ReadonlySet<TableKey>;
    isExpanded: (key: TableKey) => boolean;
    /** Bulk setter: commits one Set allocation per call. Pair with auto-expand-on-select. */
    setGroupsExpanded: (keys: TableKey[], expanded: boolean) => void;
}

/**
 * `Set<TableKey>` of expanded parent keys plus a bulk setter. All groups start
 * collapsed; consumers toggle individual keys via the returned setter.
 */
export function useExpansion(): UseExpansionResult {
    const [expandedGroups, setExpandedGroups] = useState<Set<TableKey>>(() => new Set());

    const setGroupsExpanded = useCallback((keys: TableKey[], expanded: boolean) => {
        if (keys.length === 0) return;
        setExpandedGroups((prev) => {
            const next = new Set(prev);
            let changed = false;
            for (const key of keys) {
                if (expanded && !next.has(key)) {
                    next.add(key);
                    changed = true;
                } else if (!expanded && next.has(key)) {
                    next.delete(key);
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
    }, []);

    const isExpanded = useCallback((key: TableKey) => expandedGroups.has(key), [expandedGroups]);

    return { expandedGroups, isExpanded, setGroupsExpanded };
}
